import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    fullName: state.components.contactUsForm.fieldValues["fullName"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface FullNameStyles {
    fullNameTextField?: TextFieldPresentationProps;
}

export const fullNameStyles: FullNameStyles = {};

const styles = fullNameStyles;

const FullName: React.FC<Props> = ({ fields, fullName, setFieldValue }) => {
    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [fullNameErrorMessage, setFullNameErrorMessage] = React.useState("");

    React.useEffect(() => {
        contactFormContext.validators.fullName = validateFullName;
        if (typeof fullName !== "undefined") {
            validateFullName();
        }
    }, [fullName]);

    React.useEffect(() => {
        contactFormContext.validators.fullName = validateFullName;
    }, [isRequired]);

    const validateFullName = () => {
        const errorMessage = isRequired && !fullName ? (siteMessage("Field_Required", label) as string) : "";
        setFullNameErrorMessage(errorMessage);
        return !errorMessage;
    };

    const fullNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: "fullName", value: event.target.value });
    };

    return (
        <TextField
            {...styles.fullNameTextField}
            label={label}
            required={isRequired}
            placeholder={hintText}
            value={fullName || ""}
            error={fullNameErrorMessage}
            onChange={fullNameChangeHandler}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(FullName),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Full Name",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Enter Full Name",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
